import { StaticImage } from "gatsby-plugin-image"

const aiEverythingMEAData = {
  color: "#99cdfeff",
  heroTitle: `AI avatar challenge that drove 2,000+ registrations for AI Everything MEA Egypt`,
  heroImage: (
    <StaticImage
      src="../../images/case-study/ai-everything-mea/hero.png"
      alt="ai-everything-mea-egypt-ai-summit-avatar-challenge-hero"
    />
  ),
  metrics: {
    content: `<a href="https://aieverything.me/" target="_blank">AI Everything MEA Egypt</a> was a first-edition show with a first-edition problem: not enough people knew about it yet. With two weeks to go, the team partnered with Premagic to launch the AIE Avatar Challenge: a campaign that turned registered attendees into advocates by giving them something genuinely fun to share. Within a week, LinkedIn was flooded with avatar posts, registrations picked up, and the event became the biggest sensation in Egypt's AI community that year. 

`,
    values: [
      {
        label: "Registrations Driven",
        value: "2,000+",
      },
      {
        label: "AI Avatars Generated",
        value: "5,000+",
      },
      {
        label: "LinkedIn Posts in One Week",
        value: "1,800+",
      },
    ],
  },
  images: [{}],

  caseStudyData: [
    {
      title: "The Challenge",
      subheading: `You're Competing for Attention Before the Event Even Starts`,
      info1: `AI Everything MEA Egypt is a major global AI summit hosted in Cairo by Egypt's MCIT and ITIDA, bringing together tech leaders, startups, investors, and policymakers from across the Middle East and Africa. As a new show approaching its first edition, the team had a clear goal: boost ticket sales, spread awareness across the AI community in Egypt, and drive real participation from the right people.<br/><br/>The challenge was getting that message to travel. The event hadn't yet built a reputation to lean on. Email campaigns and social posts could only go so far. They needed something that would reach the AI community organically, through people those communities already trusted.<br/><br/>`,
      info2: `<div style="background:#FEEBE7; padding:20px 24px; border-radius:4px; border-left:4px solid #ff5a2f; display:flex; align-items:flex-start; gap:16px;">
  <div style="width:4px; background:#FCC6BB; height:auto;"></div>
  <p style="margin:0; color:#4a4a4a; font-size:18px; line-height:1.6; font-family: Arial, sans-serif;">
    If your pre-event marketing relies entirely on outbound channels (email, paid social, ads), you're leaving your most powerful asset untapped: the people already registered and excited about your event.
  </p>

</div>`,
      image: (
        <StaticImage
          src="../../images/case-study/ai-everything-mea/cover.png"
          alt="AI Everything MEA Egypt challenge introduction"
        />
      ),
      imagePosition: "center",
    },
    {
      title: "The Solution",
      subheading: `Turn Your Attendees Into a Marketing Team, Automatically`,
      info1: `Premagic's <b>#AIEAvatarChallenge</b> gave AI Everything MEA Egypt's attendees something most event promotions never do: a reason to want to share.<br/><br/>Here's how it worked:<br/><br/><ul style='list-style-type: disc; padding-left: 20px;'>
  <li style='list-style-type: disc; display: list-item;'>
    Attendees visited a branded landing page and generated a <strong>personalized AI avatar</strong> — styled to match the event's visual identity.
  </li>
  <br />
  <li style='list-style-type: disc; display: list-item;'>
    With a single click, they shared their avatar to LinkedIn, automatically embedding a <strong>unique referral registration link</strong> in each post.
  </li>
  <br />
  <li style='list-style-type: disc; display: list-item;'>
    The attendee who drove the most registrations won an <strong>All-Access Pass</strong> to the event.
  </li>
</ul>`,
      info2: `<div style="background:#FEEBE7; padding:20px 24px; border-radius:4px; border-left:4px solid #ff5a2f; display:flex; align-items:flex-start; gap:16px;">
  <div style="width:4px; background:#FCC6BB; height:auto;"></div>
  <p style="margin:0; color:#4a4a4a; font-size:18px; line-height:1.6; font-family: Arial, sans-serif;">
    The lower the barrier to participate, the faster word spreads. A campaign your attendees can complete in under 60 seconds, and actually enjoy sharing, will always outperform a campaign that requires effort.
  </p>

</div>`,
      image: (
        <StaticImage
          src="../../images/case-study/ai-everything-mea/aie_avatars-setup.png"
          alt="AI Everything MEA Egypt avatar challenge flow"
        />
      ),
      imagePosition: "center",
      testimonial: {
        img: (
          <StaticImage
            src="../../images/case-study/ai-everything-mea/ahmed-alkhatib.jpeg"
            alt="ai-everything-mea-egypt-ai-summit-photo"
            className="!w-full"
            width={800}
          />
        ),
        message: `<i>"The setup was smooth and the experience was simple for attendees to use, which helped drive strong adoption and added meaningful digital engagement to our pre-show journey."</i>`,
        name: "Ahmed Alkhatib",
        info: "Dubai World Trade Centre",
      },
    },
    {
      title: "The Results",
      subheading: `Organic Reach That Paid Campaigns Can't Replicate`,
      info1: `LinkedIn was flooded. Within a week of launch, <b>1,800+ avatar posts</b> had taken over LinkedIn feeds across the AI community in Egypt. The volume of posts created its own momentum — each new post added to a growing wave of visibility that was impossible to ignore.<br/><br/><div style="background:#ffffff; padding:20px 24px; font-family: Arial, sans-serif;">
  <ul style="margin:0; padding-left:24px; color:#111; font-size:20px; line-height:1.5;">
    
    <li style="margin-bottom:8px; list-style-type: disc;">
      5,013 personalized AI avatars generated by 3,569 attendees
    </li>
    
    <li style="margin-bottom:8px; list-style-type: disc;">
      1,800+ LinkedIn posts in a single week, kickstarting a viral loop
    </li>
    
    <li style="list-style-type: disc;">
      2,000+ new registrations driven directly through referral links, all trackable
    </li>
  </ul>
</div>`,
      info2: `Every post was organic. Every share came from a real person who was already attending the event, making the promotion feel like <b>authentic peer recommendation, not advertising</b>.<br/><br/>When an attendee shares an event, it carries more trust than any ad. A single attendee with 500 LinkedIn connections is worth more than a sponsored post to a cold audience. <br/><br/><div style="background:#FEEBE7; padding:20px 24px; border-radius:4px; border-left:4px solid #ff5a2f; display:flex; align-items:flex-start; gap:16px;">
  <div style="width:4px; background:#FCC6BB; height:auto;"></div>
  <p style="margin:0; color:#4a4a4a; font-size:18px; line-height:1.6; font-family: Arial, sans-serif;">
    <b>When an attendee shares an event, it carries more trust than any ad.</b> A single attendee with 500 LinkedIn connections is worth more than a sponsored post to a cold audience. 
  </p>

</div>`,
      image: (
        <StaticImage
          src="../../images/case-study/ai-everything-mea/aie-avatars.png"
          alt="ai-everything-mea-egypt-linkedin-avatar-posts"
        />
      ),
      imagePosition: "top",
      testimonial: {
        img: (
          <StaticImage
            src="../../images/case-study/ai-everything-mea/ahmed-alkhatib.jpeg"
            alt="ai-everything-mea-egypt-ai-summit-photo"
            className="!w-full"
            width={800}
          />
        ),
        message: `<i>"We started seeing avatars shared across LinkedIn, which organically increased visibility for the event and sparked real conversations around AI in Egypt. It felt engaging rather than promotional."</i>`,
        name: "Ahmed Alkhatib",
        info: "Dubai World Trade Centre",
      },
    },
    {
      title: "Bonus: Sponsor & Brand Amplification",
      subheading:
        "Thousands of branded impressions at no extra cost to sponsors",
      info1: `Every avatar frame was fully customized with the event's branding and sponsor logos. That meant sponsors appeared in thousands of organic LinkedIn posts.`,
      info2: `<div style="background:#FEEBE7; padding:20px 24px; border-radius:4px; border-left:4px solid #ff5a2f; display:flex; align-items:flex-start; gap:16px;">
  <div style="width:4px; background:#FCC6BB; height:auto;"></div>
  <p style="margin:0; color:#4a4a4a; font-size:18px; line-height:1.6; font-family: Arial, sans-serif;">
    Offering sponsors branded avatar frames adds a <b>high-visibility, low-cost activation</b> to your sponsorship packages — one that differentiates your event from the standard logo-on-a-slide deal.
  </p>

</div><br/><br/><b>Want to bring the avatar experience to your booth or activation zone?</b> See how <a href="https://premagic.com/case-study/laqshya-live-experiences/">Laqshya Live Experiences</a> used Premagic's AI avatars as a live onsite experience to create standout moments on the show floor.
`,
      image: (
        <StaticImage
          src="../../images/case-study/ai-everything-mea/aie_avatars-etisalat.png"
          alt="ai-everything-mea-egypt-branded-sponsor-avatar-frames"
        />
      ),
      imagePosition: "top",
      testimonial: {
        img: (
          <StaticImage
            src="../../images/case-study/ai-everything-mea/ahmed-alkhatib.jpeg"
            alt="ai-everything-mea-egypt-ai-summit-photo"
            className="!w-full"
            width={800}
          />
        ),
        message: `<i>"Honestly, the AIE Avatar campaign has been a great addition to AI Everything Egypt. It gave attendees something interactive and creative beyond a typical announcement or email campaign."</i>`,
        name: "Ahmed Alkhatib",
        info: "Dubai World Trade Centre",
      },
    },
    {
      title: "Why It Works",
      subheading: "Curiosity, FOMO, and the Power of Peer Influence",
      info1: `Seeing a colleague post a sleek, personalized avatar from an AI event does two things instantly: it makes you curious about what they're attending, and it makes you not want to miss out. That combination of <b>curiosity and FOMO</b> is what turned a simple sharing campaign into a viral loop.`,
      video: "https://www.youtube.com/embed/CVKWx0pRXMA?si=n6B71GY08-QRvjml",
      imagePosition: "center",
      info2: `People share things that reflect well on them. A creative, personalized avatar tied to a major AI conference signals to a professional network that the attendee is at the forefront of their industry. As those posts multiplied across LinkedIn, they drove peers to explore the event, look it up, and register.<br/><br/>That's fundamentally different from asking attendees to share your event. This campaign gave them <b>something genuinely fun to post</b>. Combined with referral tracking and a competitive incentive, the hype built on itself over the two weeks before the event.<br/><br/>The same principle has powered results across other Premagic advocacy campaigns around the world, including the <a href="https://premagic.com/case-study/african-tech-summit/">African Tech Summit</a>, where nearly half of all attendees actively promoted the event through personalized content.<br/><br/> <a href="https://premagic.com/reports/event-advocacy-playbook/">Check out our Event Advocacy Playbook</a>`,
    },
    {
      title:
        "Ready to Turn Your Attendees Into Your Biggest Marketing Channel?",
      info1: `See how Premagic's AIE Avatar Challenge can generate viral pre-event engagement for your next conference.`,
      imagePosition: "center",
    },
  ],
  isCTAActive: true,
}

export default aiEverythingMEAData
